<template>
  <div id="unidades-da-matricula-mae">

    <card titulo="Unidades desta matrícula">

      <div v-if="!linhas.length && !loading" class="alert alert-outline-info">
        Não há unidades para exibir
      </div>

      <card-table v-if="linhas.length" class="table-borderless mb-2">
        <thead>
          <tr>
            <th style="min-width: 200px">Designação</th>
            <th style="min-width: 70px" class="text-center">Ato</th>
            <th style="min-width: 200px">Proprietário(s)</th>
            <th style="min-width: 90px" class="text-center">Área</th>
            <th style="min-width: 90px" class="text-center">Fração</th>
            <th style="min-width: 150px">Destino</th>
            <th style="width: 15px" v-if="podeEditar"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(linha, i) in linhas" :key="'unidade-mae-' + i">
            <td>
              {{linha.designacao}}
              <!-- A tarja da unidade que continua na ficha. É a MESMA palavra da aba Unidades da
                   análise ("não transmitida"), de propósito: as duas tabelas são lidas pela mesma
                   pessoa, e trocar o termo aqui a faria procurar duas coisas diferentes. Sem ela, a
                   linha de estoque se leria como uma transmissão cujo destino a leitura perdeu. -->
              <span v-if="linha.semMovimento" class="badge badge-secondary ml-1"
                    title="A ficha descreve esta unidade, nenhum ato a transmitiu e nenhuma outra matrícula a recebeu. Ela continua nesta matrícula, com quem é dono dela.">
                não transmitida
              </span>
            </td>
            <td class="text-center">{{linha.ato || '-'}}</td>
            <td>{{linha.proprietarios || '-'}}</td>
            <td class="text-center">{{linha.area || '-'}}</td>
            <td class="text-center">{{linha.fracao || '-'}}</td>
            <td>
              <a v-if="linha.fichaId" href class="icon pointer" @click.prevent="detalhes(linha)">
                {{linha.destino}}
              </a>
              <template v-else>{{linha.destino || '-'}}</template>
            </td>
            <!-- O lápis só na unidade que a ANÁLISE leu do teor: é ela que mora no card
                 "Referências" desta ficha e é aqui que se corrige. A unidade que já tem
                 matrícula própria é cadastro da OUTRA ficha, e o destino ao lado já leva até lá. -->
            <td class="text-right pr-2" style="padding-left: 0" v-if="podeEditar">
              <a v-if="linha.chaveReferencia" href class="icon" title="Editar esta unidade"
                 @click.prevent="editar(linha)">
                <i class="fa fa-edit"></i>
              </a>
            </td>
          </tr>
        </tbody>
      </card-table>

      <!-- O endereço da edição, dito na tela em que o conferente procura por ela. Sem esta linha a
           aba era um beco: os dados estavam à vista, o editor existia no formulário da ficha desde
           06/09/2026, e não havia nada aqui que ligasse um ao outro. -->
      <small class="text-muted d-block" v-if="podeEditar && linhas.length">
        Para corrigir a designação, os proprietários, a área, a fração ou a matrícula de destino de
        uma unidade, use o lápis da linha: ele abre a edição da ficha já no card
        "Referências", e a correção vale ao salvar a ficha. A unidade que já tem matrícula
        própria se edita na ficha dela, pelo link do destino.
      </small>

    </card>

    <small-loading v-if="loading"></small-loading>

  </div>
</template>

<script>

import FrontBusiness from "@/business/FrontBusiness";
import IndicadorRealBusiness from "@/business/indicadores/IndicadorRealBusiness";
import Card from "@/components/Layout/components/Card.vue";
import CardTable from "@/components/Layout/components/CardTable.vue";
import DetalhesIndicadorReal from "@/components/IndicadorReal/Detalhes/DetalhesIndicadorReal.vue";
import SmallLoading from "@/components/SmallLoading.vue";

/**
 * A aba que só existe na matrícula-mãe: uma linha por UNIDADE gerada por ela (o lote do loteamento,
 * a unidade autônoma do condomínio, a área desmembrada que ganhou ficha própria).
 *
 * Duas procedências convivem aqui, e a aba existe porque elas não se encontravam em lugar nenhum da
 * tela:
 *
 * - a unidade que JÁ tem ficha própria no acervo, lida por `listarUnidades` (a consulta inversa
 *   sobre `registro_anterior.origem_id` que a aba Registros Posteriores também consome);
 * - a unidade que a análise de matrícula LEU do teor e que ainda não virou ficha, gravada como
 *   referência na ficha da mãe (`MatriculaItnOdinBusiness.aplicarUnidadesDaMatriculaMae`). É a
 *   única fonte que traz o dono, a área e a fração por unidade. São dois tipos, e a tabela os
 *   distingue por tarja: `TRANSMISSAO` é a unidade que SAIU da ficha, `UNIDADE_DESCRITA` é a que a
 *   ficha descreve e continua tendo — o lote que ninguém comprou.
 *
 * O casamento entre as duas é pelo NÚMERO da ficha de destino, nunca pela designação: número é
 * dado, designação é texto que cada teor escreve do seu jeito, e casar por texto produziria as duas
 * falhas ao mesmo tempo — a linha repetida e a linha escondida.
 *
 * A aba não CADASTRA unidade: criar, importar, clonar, reordenar e excluir continua sendo da aba
 * Registros Posteriores, que é a tela de gestão. Esta responde à pergunta que o conferente faz ao
 * abrir uma matrícula-mãe, que é "o que saiu daqui, para quem, e sob que fração".
 *
 * O que ela passou a fazer é ENDEREÇAR a correção. A unidade lida do teor é uma linha do card
 * "Referências", e os cinco campos dela ganharam editor em 06/09/2026 — no formulário da
 * ficha, que é outra tela. Quem estava aqui olhando os dados errados não tinha como saber disso: o
 * lápis da linha abre aquele editor já com a unidade certa aberta, e a gravação continua sendo a da
 * ficha inteira, com a validação e o histórico de sempre.
 */
export default {
  name: 'UnidadesDaMatriculaMae',
  components: {SmallLoading, CardTable, Card},

  props: {
    ficha: Object,
    /**
     * As unidades já consultadas por quem hospeda a aba. Chega preenchida no caminho normal: o
     * DetalhesIndicadorReal precisa da lista ANTES de decidir se mostra a aba, e reconsultar aqui
     * faria a mesma pergunta duas vezes na abertura da mesma ficha.
     */
    unidades: Array,
    /**
     * A ficha aberta em PRÉ-VISUALIZAÇÃO, quando é o caso. Desce do modal de detalhes e serve a uma
     * coisa só aqui: pré-visualização não é ficha gravada, e oferecer edição sobre ela levaria o
     * conferente a um formulário que não corresponde ao que ele está vendo.
     */
    preview: Object
  },

  data() {
    return {
      loading: false,
      registros: []
    }
  },

  async mounted() {
    if (Array.isArray(this.unidades)) {
      this.registros = this.unidades;
      return;
    }
    this.loading = true;
    this.registros = await IndicadorRealBusiness.listarUnidades(this.ficha.id).catch(() => []) || [];
    this.loading = false;
  },

  computed: {

    /**
     * O lápis aparece? A régua é a MESMA do ícone de editar do cabeçalho do modal de detalhes
     * (`DetalhesIndicadorReal`): auditor não edita, e pré-visualização não é ficha gravada. Duas
     * réguas diferentes para o mesmo destino dariam a um auditor um caminho que o cabeçalho lhe
     * nega.
     */
    podeEditar() {
      return !this.$root.usuarioLogado?.auditor && !this.preview;
    },

    /**
     * As unidades que a análise leu do teor e gravou na ficha da mãe, já interpretadas.
     *
     * A COLUNA vence a frase. Desde 06/09/2026 designação, proprietários, área, fração e matrícula
     * de destino são campos de `referencia_imovel`; antes disso tudo era serializado em `descricao`
     * e lido de volta por `interpretarDescricao`. Não houve backfill, então as duas gerações
     * convivem — e o parse continua atendendo também a referência que o cartório digitou à mão.
     */
    unidadesLidasDoTeor() {
      return (this.ficha?.referencias || [])
        .filter(r => r && IndicadorRealBusiness.eReferenciaDeUnidade(r.tipo))
        .map(r => {
          const partes = this.interpretarDescricao(r.descricao);
          const semMovimento = r.tipo === 'UNIDADE_DESCRITA';
          // A unidade sem movimento não tem destino, e o parse da frase antiga não pode inventar um
          // para ela: a linha existe justamente porque nenhuma outra ficha a recebeu.
          const destino = semMovimento ? null : (r.matriculaDestino || partes.destino);
          return {
            designacao: r.designacao || partes.designacao,
            ato: r.ato,
            proprietarios: r.proprietarios || partes.proprietarios,
            area: r.area || partes.area,
            fracao: this.fracaoLegivel(r.fracao) || partes.fracao,
            destino: destino ? `Matrícula ${destino}` : null,
            codigoDestino: destino,
            fichaId: null,
            semMovimento,
            // Como o editor da ficha reencontra ESTA linha. Vai por conteúdo, e não por índice:
            // as duas telas carregam a ficha por consultas próprias, e um índice casaria a linha
            // errada no dia em que a ordem mudasse.
            chaveReferencia: {ato: r.ato, designacao: r.designacao, descricao: r.descricao}
          };
        })
        // Ato ausente vai para o FIM, e não para o começo como "zero". A unidade sem movimento não
        // tem ato por construção, e ordená-la como ato 0 abriria a tabela pelo que nunca aconteceu,
        // empurrando para baixo a cadeia que o conferente vem ler.
        .sort((a, b) => {
          const x = a.ato == null ? Infinity : Number(a.ato);
          const y = b.ato == null ? Infinity : Number(b.ato);
          return x - y;
        });
    },

    linhas() {
      const lidas = this.unidadesLidasDoTeor;

      // Os números de destino que a leitura já nomeou. É por eles que a ficha própria deixa de
      // virar uma segunda linha da mesma unidade.
      const jaNomeadas = new Set(lidas.map(u => this.somenteDigitos(u.codigoDestino)).filter(Boolean));

      const comFicha = (this.registros || [])
        .filter(u => u && !jaNomeadas.has(this.somenteDigitos(u.codigo)))
        .map(u => ({
          designacao: this.designacaoDaFicha(u),
          ato: this.atoDaReferenciaDaFicha(u),
          proprietarios: null,
          area: null,
          fracao: null,
          destino: FrontBusiness.nomearFicha(
            {livro: u.livro, codigo: u.codigo, status: u.status, livroLetra: u.livroLetra}, true, true),
          codigoDestino: u.codigo,
          fichaId: u.id
        }));

      // A ficha própria, quando existe, completa a linha da unidade lida: o conferente clica dali
      // para abrir a matrícula da unidade.
      const porNumero = {};
      (this.registros || []).forEach(u => {
        const chave = this.somenteDigitos(u?.codigo);
        if (chave) porNumero[chave] = u;
      });
      lidas.forEach(linha => {
        const ficha = porNumero[this.somenteDigitos(linha.codigoDestino)];
        if (!ficha) return;
        linha.fichaId = ficha.id;
        linha.destino = FrontBusiness.nomearFicha(
          {livro: ficha.livro, codigo: ficha.codigo, status: ficha.status, livroLetra: ficha.livroLetra},
          true, true);
      });

      return [...lidas, ...comFicha];
    }
  },

  methods: {

    /**
     * A fração da COLUNA como o conferente a lê: sem zero à direita, com vírgula e com o sinal.
     *
     * A coluna é numérica (`numeric(15,10)`), então ela chega como "12.5000000000"; a frase antiga
     * já trazia "12,5%" pronto, montado no backend por `fracaoLegivel`. As duas formas precisam sair
     * iguais na tela, senão a mesma unidade muda de aparência conforme a data em que foi gravada.
     */
    fracaoLegivel(valor) {
      if (valor === null || valor === undefined || valor === '') return null;
      const numero = Number(valor);
      if (Number.isNaN(numero)) return null;
      return `${String(numero).replace('.', ',')}%`;
    },

    /**
     * Desmonta a descrição que a análise grava por unidade:
     * "Lote 7, quadra 11: Fulano de Tal; área 324m²; fração 12,5%; matrícula 4101".
     *
     * Texto que não segue essa forma, como a referência que o cartório digitou à mão, vira
     * designação inteira, sem campo nenhum preenchido. É o desfecho correto: inventar dono ou área
     * a partir de um texto livre seria pior do que a célula vazia.
     */
    interpretarDescricao(descricao) {
      const vazio = {designacao: '', proprietarios: null, area: null, fracao: null, destino: null};
      const texto = (descricao || '').trim();
      if (!texto) return vazio;

      const corte = texto.indexOf(': ');
      if (corte < 0) return {...vazio, designacao: texto};

      const resultado = {...vazio, designacao: texto.substring(0, corte).trim()};
      texto.substring(corte + 2).split(';').forEach(bruto => {
        const parte = bruto.trim();
        if (!parte) return;
        if (parte.startsWith('área ')) {
          resultado.area = parte.substring('área '.length).trim();
        } else if (parte.startsWith('fração ')) {
          resultado.fracao = parte.substring('fração '.length).trim();
        } else if (parte.startsWith('matrícula ')) {
          resultado.destino = parte.substring('matrícula '.length).trim();
        } else if (!resultado.proprietarios) {
          resultado.proprietarios = parte;
        }
      });
      return resultado;
    },

    /** A designação da unidade que já tem ficha, montada dos campos que o cadastro dela preenche. */
    designacaoDaFicha(unidade) {
      const partes = [];
      if (unidade.unidade) partes.push(`Unidade ${unidade.unidade}`);
      if (unidade.lote) partes.push(`Lote ${unidade.lote}`);
      if (unidade.quadra) partes.push(`Quadra ${unidade.quadra}`);
      if (unidade.bloco) partes.push(`Bloco ${unidade.bloco}`);
      if (unidade.pavimento) partes.push(`Pavimento ${unidade.pavimento}`);
      if (partes.length) return partes.join(', ');
      return FrontBusiness.nomearFicha(
        {livro: unidade.livro, codigo: unidade.codigo, status: unidade.status}, true, true);
    },

    /**
     * O ato que ligou a unidade à mãe, quando a ficha da unidade tem referência gravada. O campo
     * `referencias` vem como texto JSON do `listarUnidades`, exatamente como a aba Registros
     * Posteriores o recebe.
     */
    atoDaReferenciaDaFicha(unidade) {
      let referencias = unidade.referencias;
      if (typeof referencias === 'string') {
        try {
          referencias = JSON.parse(referencias || '[]');
        } catch (e) {
          referencias = [];
        }
      }
      const comAto = (referencias || []).filter(r => r && r.ato != null);
      return comAto.length ? comAto[0].ato : null;
    },

    somenteDigitos(valor) {
      const texto = `${valor == null ? '' : valor}`.replace(/\D/g, '');
      return texto || null;
    },

    detalhes(linha) {
      FrontBusiness.openModal(DetalhesIndicadorReal, {id: linha.fichaId});
    },

    /**
     * Abre o editor da ficha já no card "Referências", com a unidade desta linha aberta.
     *
     * O `load` de volta é o que faz a correção aparecer aqui sem o conferente fechar e reabrir a
     * matrícula — esta aba lê `ficha.referencias`, que só muda quando o pai reconsulta.
     */
    async editar(linha) {
      // Importado aqui dentro, e não no topo: o formulário da ficha alcança de volta este mesmo
      // arquivo pela cadeia do card de referências, e o import estático fecharia um ciclo que
      // deixa um dos módulos indefinido no momento em que o outro é avaliado.
      const {default: FormIndicadorReal} = await import('@/components/IndicadorReal/Form/FormIndicadorReal.vue');
      const salvou = await FrontBusiness.openModal(FormIndicadorReal, {
        id: this.ficha.id, livro: this.ficha.livro, abrirReferencia: linha.chaveReferencia
      });
      if (salvou) this.$emit('load');
    }
  }
}
</script>
